import classNames from 'classnames';
import React, { useContext, useEffect } from 'react';
import { ACTION_TYPES, AppContext } from '../AppContext';
import { CAMEL_LETTERS, GUESS_COUNT } from '../consts';
import { gameWords } from '../words';
import DeleteIcon from './DeleteIcon';
import EnterIcon from './EnterIcon';

function deleteHandler(input, dispatch) {
  input.pop();
  dispatch({ type: ACTION_TYPES.UPDATE_INPUT, payload: input });
}

const Keyboard = () => {
  const [{ input = [], wordOne, wordOneDone, wordTwo, wordTwoDone, guesses = [] }, dispatch] = useContext(AppContext);

  const enterHandler = (input, dispatch) => {
    const isValidWord = gameWords.includes(input.join(''));
    if (isValidWord) {
      dispatch({ type: ACTION_TYPES.ADD_NEW_GUESS, payload: input });
    } else {
      dispatch({ type: ACTION_TYPES.UPDATE_SHAKE, payload: true });
      setTimeout(() => {
        dispatch({ type: ACTION_TYPES.UPDATE_SHAKE, payload: false });
      }, 1000);
    }
  };

  const stopTyping = guesses.length === GUESS_COUNT || (wordTwoDone && wordOneDone);
  const canSubmit = input.length === 5;

  const onClick = (e) => {
    const element = e.target.closest('button');
    const key = element.dataset.type;

    if (stopTyping) {
      return;
    }
    if (key === 'DELETE') {
      deleteHandler(input, dispatch);
      return;
    }
    if (key === 'ENTER') {
      if (canSubmit) {
        enterHandler(input, dispatch);
      }
      return;
    }
    if (input.length > 4) {
      return;
    }
    dispatch({ type: ACTION_TYPES.UPDATE_INPUT, payload: [...input, key] });
  };

  useEffect(() => {
    const handleKeyUp = ({ key }) => {
      if (stopTyping) {
        return;
      }
      if (key === 'Backspace') {
        deleteHandler(input, dispatch);
      }
      if (key === 'Enter' && canSubmit) {
        enterHandler(input, dispatch);
      }

      if (input.length > 4) {
        return;
      }

      if (CAMEL_LETTERS.flat().includes(key)) {
        dispatch({ type: ACTION_TYPES.UPDATE_INPUT, payload: [...input, key] });
      }
    };

    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [dispatch, input, stopTyping, canSubmit]);

  const allGuessedLetters = guesses.flat();

  return (
    <div style={{ maxWidth: '600px' }} className='w-full font-semibold text-lg mt-5 md:mt-0 mx-auto'>
      <div className='w-full'>
        {CAMEL_LETTERS.map((row, i) => (
          <div className='flex w-full justify-center' key={i}>
            {row.map((letter) => {
              const isLetterUsed = allGuessedLetters.includes(letter);
              let isLetterCorrectOnWordOne = false;
              let isLetterCorrectOnWordTwo = false;
              const isLetterExistOnWordOne = wordOne.includes(letter);
              const isLetterExistOnWordTwo = wordTwo.includes(letter);

              guesses.forEach((guess) => {
                guess.forEach((guessLetter, letterIndex) => {
                  if (wordOne[letterIndex] === guessLetter && guessLetter === letter) {
                    isLetterCorrectOnWordOne = true;
                  }
                  if (wordTwo[letterIndex] === guessLetter && guessLetter === letter) {
                    isLetterCorrectOnWordTwo = true;
                  }
                });
              });

              const isDelete = letter === 'DELETE';
              const isEnter = letter === 'ENTER';
              const yellowRGBA = 'rgb(234, 179, 8)';
              const greenRGBA = 'rgb(22, 163, 74)';
              const grayRGBA = 'rgb(107, 114, 128)';
              const darkGrayRGBA = 'rgb(55, 65, 81)';

              const className = classNames(
                'uppercase h-12 font-semibold rounded-sm m-0.5 md:m-1 text-white border hover:-translate-y-0.5 active:bg-cyan-500  active:border-cyan-500',
                {
                  'bg-gray-500 border-gray-500': !isLetterUsed,
                  'bg-gray-700 border border-gray-700': isLetterUsed,
                  'w-[calc(8%)]': !isDelete && !isEnter,
                  'w-[calc(9%)] py-1': isDelete || isEnter,
                }
              );
              const wordOneColor = isLetterCorrectOnWordOne
                ? greenRGBA
                : isLetterExistOnWordOne
                ? yellowRGBA
                : isLetterUsed
                ? darkGrayRGBA
                : grayRGBA;

              const wordTwoColor = isLetterCorrectOnWordTwo
                ? greenRGBA
                : isLetterExistOnWordTwo
                ? yellowRGBA
                : isLetterUsed
                ? darkGrayRGBA
                : grayRGBA;

              const letterStyle = isLetterUsed ? { background: `linear-gradient(90deg, ${wordOneColor} 50%, ${wordTwoColor} 50%)` } : {};

              return (
                <button style={letterStyle} data-type={letter} onClick={onClick} key={letter} className={className}>
                  {isDelete ? <DeleteIcon /> : isEnter ? <EnterIcon /> : letter}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Keyboard;
